import { PayoutFrequencyType } from "@modules/box/boxPayoutFrequencyTypes"
import { type BoxApiGateway } from "@modules/box/services/boxApi.gateway"
import {
  type BoxDetailDto,
  type BoxSettingsInputDto,
  type BoxSettingsWaiverDto,
  type WebBoxInputDto,
} from "@modules/box/services/boxDto"
import { BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { PaymentMethodMode, PaymentMethodType } from "@modules/finances/types/financesType"
import { StripeAccountStatus, StripePlatform } from "@modules/payment/stripeTypes"
import { USER_TEST_DATA } from "@modules/profile/publicProfile/test/publicProfileApi.fake"
import { v4 as uuidv4 } from "uuid"

const _BOX_FAKE_DATA_DEFAULT: BoxDetailDto = {
  id: "box_default",
  logo: { url: "logo_url", hash: "logo_hash" },
  cover: { url: "cover_url", hash: "cover_hash" },
  name: "Box default name",
  address: {
    address: "Address default",
    additionalInformation: "Additional information default",
    city: "City default",
    postalCode: "12345",
    country: "Country default",
  },
  isSubscribed: true,
  photos: [],
  description: "Box default description",
  phoneNumberWithPrefix: {
    number: "0123456789",
    countryCode: "FR",
  },
  email: "box.default@hustle-up.app",
  siren: "0123456789",
  owner: USER_TEST_DATA.OWNER,
  stripeAccountCurrency: "EUR",
  stripeAccountStatus: StripeAccountStatus.OK,
  stripeAccountId: "stripe_account_id",
  stripeMaximumAmount: null,
  stripeMinimumAmount: null,
  stripePlatform: StripePlatform.FR,
  currentUserRole: BoxStaffRole.OWNER,
  accountError: null,
  location: null,
  slotGeneratorInWeeks: null,
  zoneId: "Europe/Paris",
  paymentMethodPreferences: [
    {
      type: PaymentMethodType.CARD,
      mode: PaymentMethodMode.SUBSCRIPTION,
      enabled: true,
    },
    {
      type: PaymentMethodType.SEPA_DEBIT,
      mode: PaymentMethodMode.SUBSCRIPTION,
      enabled: true,
    },
    {
      type: PaymentMethodType.CARD,
      mode: PaymentMethodMode.PAYMENT,
      enabled: true,
    },
  ],
  waiver: null,
  invitationLink: null,
  showSessionAttendees: true,
  taxStatus: null,
  payoutFrequencyType: PayoutFrequencyType.MONTHLY,
  payoutFrequencyDayCalendar: 5,
  weeklyAnchorStripe: null,
  biweeklyPeriod: null,
  createdAt: "2023-01-15T10:00:00Z",
}

export const BOX_TEST_DATA = {
  BOX: { DEFAULT: _BOX_FAKE_DATA_DEFAULT },
  BOXES: [_BOX_FAKE_DATA_DEFAULT],
}

export class FakeBoxApi implements BoxApiGateway {
  boxes: BoxDetailDto[] = []

  givenBoxes(boxes: BoxDetailDto[]) {
    this.boxes = boxes
  }

  async getBoxs() {
    return Promise.resolve(this.boxes.map((box) => ({ ...box, _type: "BOX" as const, role: BoxStaffRole.OWNER })))
  }

  async getBox(id: string) {
    const index = this.boxes.findIndex((box) => box.id === id)

    if (index >= 0) return Promise.resolve(this.boxes[index]!)
    else throw new Error("Missing box")
  }

  async createBox(box: WebBoxInputDto): Promise<BoxDetailDto> {
    const newBox: BoxDetailDto = {
      ...box,
      id: uuidv4(),
      owner: USER_TEST_DATA.OWNER,
      isSubscribed: false,
      logo: { url: "logo_url", hash: "logo_hash" },
      cover: { url: "cover_url", hash: "cover_hash" },
      photos: [],
      stripeAccountCurrency: "EUR",
      stripeAccountStatus: null,
      stripeAccountId: null,
      stripeMaximumAmount: null,
      stripeMinimumAmount: null,
      stripePlatform: StripePlatform.FR,
      currentUserRole: BoxStaffRole.OWNER,
      accountError: null,
      location: null,
      slotGeneratorInWeeks: null,
      zoneId: "Europe/Paris",
      paymentMethodPreferences: [
        {
          type: PaymentMethodType.CARD,
          mode: PaymentMethodMode.SUBSCRIPTION,
          enabled: true,
        },
        {
          type: PaymentMethodType.SEPA_DEBIT,
          mode: PaymentMethodMode.SUBSCRIPTION,
          enabled: true,
        },
        {
          type: PaymentMethodType.CARD,
          mode: PaymentMethodMode.PAYMENT,
          enabled: true,
        },
      ],
      waiver: null,
      invitationLink: null,
      showSessionAttendees: true,
      taxStatus: null,
      payoutFrequencyType: PayoutFrequencyType.MONTHLY,
      payoutFrequencyDayCalendar: 5,
      weeklyAnchorStripe: null,
      biweeklyPeriod: null,
      createdAt: new Date().toISOString(),
      description: box.description,
    }
    this.boxes.push(newBox)
    return Promise.resolve(newBox)
  }

  async updateBox(id: string, box: WebBoxInputDto): Promise<BoxDetailDto> {
    const updatedBoxIndex = this.boxes.findIndex((b) => id === b.id)

    const updatedBox: BoxDetailDto = {
      ...this.boxes[updatedBoxIndex]!,
      ...box,
      logo: { url: "logo_url", hash: "logo_hash" },
      cover: { url: "cover_url", hash: "cover_hash" },
      photos: [],
      waiver: null,
    }
    this.boxes[updatedBoxIndex] = updatedBox
    return Promise.resolve(updatedBox)
  }

  async updateBoxSettings(id: string, boxSettings: BoxSettingsInputDto): Promise<BoxDetailDto> {
    const updatedBoxIndex = this.boxes.findIndex((b) => id === b.id)

    const updatedBox: BoxDetailDto = {
      ...this.boxes[updatedBoxIndex]!,
      ...boxSettings,
      waiver: null,
    }
    this.boxes[updatedBoxIndex] = updatedBox
    return Promise.resolve(updatedBox)
  }

  async updateBoxWaiver(id: string, _waiver: BoxSettingsWaiverDto): Promise<BoxDetailDto> {
    const updatedBoxIndex = this.boxes.findIndex((b) => id === b.id)
    this.boxes[updatedBoxIndex]!.waiver = {
      url: "waiver_url",
      key: "waiver_key",
      fileName: "waiver_filename",
      originalFileName: "waiver_original_filename",
      uploadedAt: "waiver_uploaded_at",
      version: 1,
    }
    return Promise.resolve(this.boxes[updatedBoxIndex]!)
  }

  async deleteBox(_: string) {}
}
